/**
 * CLI Environment Management
 *
 * List, create, and set the default environment for an application, including
 * `type='sandbox'` environments backed by a verified sandbox template. Reuses
 * the existing `/org/properties/:id/environments` endpoints — no new API.
 */

import process from 'node:process';
import { resolveConfig, hasFlag, getArgValue } from './auth.js';
import { fetchProperties, apiRequest } from './cli-helpers.js';

type EnvironmentItem = {
  id: string;
  name: string;
  type: 'permanent' | 'ephemeral' | 'local' | 'sandbox';
  url: string;
  isDefault: boolean;
  sandboxTemplateId: string | null;
  environmentKindId: string | null;
  environmentKindName: string | null;
  /**
   * Values this environment supplies for the application's declared variables,
   * injected at run time as `$var.ENV.<name>`. Values of variables the property
   * marks sensitive arrive masked, not in the clear.
   */
  envVarValues: Record<string, string>;
};

/**
 * Flags in `set-var` that consume the following argv entry as their value.
 * Keep in lockstep with the `Set-var options` block in `printHelp` — a
 * value-taking flag missing here has its value parsed as a `NAME=value` pair.
 */
const SET_VAR_VALUE_FLAGS = new Set(['--application', '--property', '--environment']);

/**
 * Collect the positional `NAME=value` pairs of a `set-var` invocation, skipping
 * flags and the values they consume (a flag value may itself contain `=`).
 *
 * An empty value is legal — `NAME=` sets the variable to the empty string, which
 * is distinct from leaving it unset.
 */
export function parseEnvVarAssignments(argv: string[]): Record<string, string> {
  const assignments: Record<string, string> = {};
  for (let i = 0; i < argv.length; i++) {
    const arg = argv[i];
    if (SET_VAR_VALUE_FLAGS.has(arg)) {
      i++;
      continue;
    }
    if (arg.startsWith('--')) continue;

    const separator = arg.indexOf('=');
    if (separator <= 0) {
      throw new Error(`Expected NAME=value, got "${arg}".`);
    }
    const name = arg.slice(0, separator);
    // Mirrors the server's `EnvVarValues` name regex so an obvious typo fails
    // before the round trip. The server remains the authority and additionally
    // caps name and value length, which is left to it rather than duplicated.
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
      throw new Error(
        `Invalid variable name "${name}" — names must be alphanumeric/underscore and may not start with a digit.`
      );
    }
    // A repeated name is last-wins, matching how the shell treats repeated flags.
    assignments[name] = arg.slice(separator + 1);
  }
  return assignments;
}

function pad(s: string, n: number): string {
  return s.padEnd(n);
}

function printHelp(): void {
  console.log(
    [
      'canary environment: Manage application environments (alias: canary env)',
      '',
      'Sub-commands:',
      '  list [options]                    List environments for an application',
      '  create [options]                  Create an environment (incl. sandbox)',
      '  set-default [options]             Set an environment as the application default',
      '  set-var [options] NAME=value ...  Set per-environment variable values',
      '',
      'List options:',
      '  --application <name|id>           Application (required; alias: --property)',
      '',
      'Create options:',
      '  --application <name|id>           Application (required; alias: --property)',
      '  --name <name>                     Environment name (required)',
      '  --stage <name|id>                 Shared deployment stage (e.g. staging)',
      '  --type <permanent|ephemeral|local|sandbox>   Environment type (default: permanent)',
      '  --url <url>                       Base URL (required unless --type sandbox)',
      '  --sandbox-template <uuid>         Backing template (required iff --type sandbox)',
      '  --login-path </path>              Per-env login path override',
      '  --description <text>              Optional description',
      '  --default                         Set this environment as the application default',
      '',
      'Set-default options:',
      '  --application <name|id>           Application (required; alias: --property)',
      '  --environment <name|id>           Environment to make default (required)',
      '',
      'Set-var options:',
      '  --application <name|id>           Application (required; alias: --property)',
      '  --environment <name|id>           Environment to update (required)',
      '',
      '  Values merge into the environment; variables you do not name keep their',
      '  current value. Injected at run time as $var.ENV.<name>.',
      '',
      '  canary env set-var --application Soapbox --environment "Testing 009" \\',
      '    salesforce_url=https://example.my.salesforce.com',
      '',
      'Common options:',
      '  --json                            Output as JSON',
    ].join('\n')
  );
}

/** Resolve `--application`/`--property` (name or id) to a property id. */
async function resolveApplicationId(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<string> {
  const arg = getArgValue(argv, '--application') ?? getArgValue(argv, '--property');
  if (!arg) {
    console.error('Error: --application <name|id> is required.');
    process.exit(1);
  }
  const properties = await fetchProperties(apiUrl, token);
  const match = properties.find((p) => p.id === arg || p.name.toLowerCase() === arg.toLowerCase());
  if (!match) {
    console.error(`Error: Application "${arg}" not found.`);
    if (properties.length > 0) {
      console.error('Available applications:');
      for (const p of properties) console.error(`  - ${p.name} (${p.id})`);
    }
    process.exit(1);
  }
  return match.id;
}

async function fetchEnvironments(
  propertyId: string,
  apiUrl: string,
  token: string
): Promise<EnvironmentItem[]> {
  const res = await apiRequest<{ ok: boolean; items?: EnvironmentItem[]; error?: string }>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/environments`
  );
  if (!res.ok) {
    console.error(`Error: ${res.error ?? 'Failed to list environments'}`);
    process.exit(1);
  }
  return res.items ?? [];
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const propertyId = await resolveApplicationId(argv, apiUrl, token);
  const items = await fetchEnvironments(propertyId, apiUrl, token);

  if (json) {
    console.log(JSON.stringify(items, null, 2));
    return;
  }
  if (items.length === 0) {
    console.log('No environments found.');
    return;
  }
  console.log(
    `${pad('Name', 28)} ${pad('Stage', 14)} ${pad('Type', 11)} ${pad('Default', 8)} ${pad('URL', 36)} Template`
  );
  for (const e of items) {
    console.log(
      `${pad(e.name, 28)} ${pad(e.environmentKindName ?? '', 14)} ${pad(e.type, 11)} ${pad(e.isDefault ? 'yes' : '', 8)} ` +
        `${pad(e.url || '(none)', 36)} ${e.sandboxTemplateId ?? ''}`
    );
  }
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const propertyId = await resolveApplicationId(argv, apiUrl, token);
  const name = getArgValue(argv, '--name');
  const type = (getArgValue(argv, '--type') ?? 'permanent') as EnvironmentItem['type'];
  const url = getArgValue(argv, '--url');
  const sandboxTemplateId = getArgValue(argv, '--sandbox-template');
  const loginPath = getArgValue(argv, '--login-path');
  const description = getArgValue(argv, '--description');
  const stage = getArgValue(argv, '--stage');
  const isDefault = hasFlag(argv, '--default');

  if (!name) {
    console.error('Error: --name is required.');
    process.exit(1);
  }
  if (type === 'sandbox' && !sandboxTemplateId) {
    console.error('Error: --sandbox-template <uuid> is required when --type sandbox.');
    process.exit(1);
  }
  if (type !== 'sandbox' && !url) {
    console.error(`Error: --url is required for --type ${type}.`);
    process.exit(1);
  }

  const body: Record<string, unknown> = { name, type };
  if (url !== undefined) body.url = url;
  if (sandboxTemplateId !== undefined) body.sandboxTemplateId = sandboxTemplateId;
  if (loginPath !== undefined) body.loginPath = loginPath;
  if (description !== undefined) body.description = description;
  if (stage !== undefined) {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(stage)) {
      body.environmentKindId = stage;
    } else {
      body.environmentKindName = stage;
    }
  }
  if (isDefault) body.isDefault = true;

  const result = await apiRequest<{ ok: boolean; item?: EnvironmentItem; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${propertyId}/environments`,
    body
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  if (json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }
  console.log(`Created ${type} environment "${name}" (${result.item?.id ?? '?'})`);
}

async function handleSetDefault(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const propertyId = await resolveApplicationId(argv, apiUrl, token);
  const envArg = getArgValue(argv, '--environment');
  if (!envArg) {
    console.error('Error: --environment <name|id> is required.');
    process.exit(1);
  }

  const items = await fetchEnvironments(propertyId, apiUrl, token);
  const match = resolveEnvironment(items, envArg);

  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${propertyId}/environments/${match.id}/set-default`
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  if (json) {
    console.log(JSON.stringify({ ok: true, environmentId: match.id }, null, 2));
    return;
  }
  console.log(`Set "${match.name}" (${match.id}) as the default environment.`);
}

/** Resolve `--environment` (name or id) against the application's environments. */
function resolveEnvironment(items: EnvironmentItem[], envArg: string): EnvironmentItem {
  const match = items.find((e) => e.id === envArg || e.name.toLowerCase() === envArg.toLowerCase());
  if (!match) {
    console.error(`Error: Environment "${envArg}" not found for this application.`);
    if (items.length > 0) {
      console.error('Available environments:');
      for (const e of items) console.error(`  - ${e.name} (${e.id})`);
    }
    process.exit(1);
  }
  return match;
}

export async function handleSetVar(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const propertyId = await resolveApplicationId(argv, apiUrl, token);
  const envArg = getArgValue(argv, '--environment');
  if (!envArg) {
    console.error('Error: --environment <name|id> is required.');
    process.exit(1);
  }

  let assignments: Record<string, string>;
  try {
    assignments = parseEnvVarAssignments(argv);
  } catch (err) {
    console.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }
  if (Object.keys(assignments).length === 0) {
    console.error('Error: at least one NAME=value assignment is required.');
    process.exit(1);
  }

  const items = await fetchEnvironments(propertyId, apiUrl, token);
  const match = resolveEnvironment(items, envArg);

  // The update endpoint replaces `envVarValues` wholesale, so the full map goes
  // back or every unlisted variable is destroyed. Masked sensitive values ride
  // along untouched — the server substitutes the stored plaintext for any entry
  // equal to the mask, the same round-trip the UI relies on.
  //
  // This read-then-write is last-writer-wins: a concurrent edit landing between
  // the two calls is overwritten with this snapshot. The endpoint offers no
  // compare-and-swap, so avoid setting variables on one environment from two
  // places at once.
  const envVarValues = { ...match.envVarValues, ...assignments };

  const result = await apiRequest<{ ok: boolean; item?: EnvironmentItem; error?: string }>(
    apiUrl,
    token,
    'PUT',
    `/org/properties/${propertyId}/environments/${match.id}`,
    { envVarValues }
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  if (json) {
    console.log(
      JSON.stringify(
        { ok: true, environmentId: match.id, envVarValues: result.item?.envVarValues ?? {} },
        null,
        2
      )
    );
    return;
  }
  const names = Object.keys(assignments).join(', ');
  console.log(`Set ${names} on "${match.name}" (${match.id}).`);
}

export async function runEnvironment(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  const globalFlags = new Set(['--env', '--api-url', '--token', '--org']);
  const args = rest.filter((arg, i) => {
    if (globalFlags.has(arg)) return false;
    if (i > 0 && globalFlags.has(rest[i - 1])) return false;
    return true;
  });

  switch (subCommand) {
    case 'list':
      await handleList(args, apiUrl, token);
      break;
    case 'create':
      await handleCreate(args, apiUrl, token);
      break;
    case 'set-default':
      await handleSetDefault(args, apiUrl, token);
      break;
    case 'set-var':
      await handleSetVar(args, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printHelp();
      process.exit(1);
  }
}
